/**
 * The parent-facing brand profile switcher.
 *
 * Renders as the brand's own logo rather than a generic dropdown so the header itself tells a
 * parent which catalog they are in — the thing the switcher exists to make unambiguous. It is
 * hidden entirely when only one brand is known, so a single-brand deployment looks exactly as
 * it did before this existed.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useBrandProfile } from '../contexts/brand-profile';
import { BRAND_PROFILES, brandProfileOf } from '../config/brandProfiles';
import type { BrandKey } from '../config/brands';
import Img from './ui/Img';

function BrandMark({ brand, className }: { brand: BrandKey; className?: string }) {
    const { logoPath } = BRAND_PROFILES[brand];
    // No artwork: the name beside it already says which brand this is.
    if (!logoPath) return null;
    return <Img src={logoPath} alt="" className={className} width={512} height={512} />;
}

interface BrandProfileSwitcherProps {
    /** Tighter padding and type, for the phone header. */
    compact?: boolean;
    /**
     * Which edge of the button the menu hangs from.
     *
     * The menu is 18rem wide, so it must open *inwards*. Anchored to the wrong edge it runs
     * off the screen — which is what happened when the switcher moved to the left of the
     * mobile header and kept its `right-0`: the panel extended ~190px past the left edge and
     * its first column of text was unreadable. `max-w` caps the width but cannot pull back
     * something already positioned off-canvas.
     *
     * Defaults to `right`, which is correct for the desktop header where the switcher sits on
     * the right-hand side.
     */
    align?: 'left' | 'right';
}

export function BrandProfileSwitcher({ compact = false, align = 'right' }: BrandProfileSwitcherProps) {
    const { activeBrand, setActiveBrand, availableBrands, memberBrands } = useBrandProfile();
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        function onPointerDown(event: MouseEvent) {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setIsOpen(false);
        }
        function onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') setIsOpen(false);
        }
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    // Nothing to switch between.
    if (availableBrands.length < 2) return null;

    const active = brandProfileOf(activeBrand);

    const choose = (brand: BrandKey) => {
        setIsOpen(false);
        if (brand === activeBrand) return;
        setActiveBrand(brand);
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                onClick={() => setIsOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={`Switch program profile — currently ${active.shortName}`}
                className={`flex items-center rounded-full border transition-colors ${
                    compact ? 'gap-1 pl-1.5 pr-2 py-1' : 'gap-2 pl-2 pr-2.5 py-1.5'
                }`}
                style={{
                    borderColor: active.accent,
                    backgroundColor: active.accentSoft,
                    color: active.accentInk,
                }}
            >
                <BrandMark brand={active.key} className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
                <span className={`font-semibold whitespace-nowrap ${compact ? 'text-xs' : 'text-sm'}`}>
                    {active.shortName}
                </span>
                <ChevronDown className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            </button>

            {isOpen && (
                <div
                    role="menu"
                    className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50`}
                >
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        Program profile
                    </p>
                    {availableBrands.map(key => {
                        const profile = BRAND_PROFILES[key];
                        const isActive = key === activeBrand;
                        const isMember = memberBrands.includes(key);
                        return (
                            <button
                                key={key}
                                role="menuitemradio"
                                aria-checked={isActive}
                                onClick={() => choose(key)}
                                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-gray-50 ${
                                    isActive ? 'bg-gray-50' : ''
                                }`}
                            >
                                {profile.logoPath && (
                                    <span
                                        className="flex-shrink-0 mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg"
                                        style={{ backgroundColor: profile.accentSoft }}
                                    >
                                        <BrandMark brand={key} className="h-6 w-6" />
                                    </span>
                                )}
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-gray-900 truncate">
                                            {profile.shortName}
                                        </span>
                                        {isMember ? (
                                            <span className="flex-shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                                Enrolled
                                            </span>
                                        ) : (
                                            <span
                                                className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                                                style={{ backgroundColor: profile.accentSoft, color: profile.accentInk }}
                                            >
                                                Explore
                                            </span>
                                        )}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-gray-500">{profile.tagline}</span>
                                </span>
                                {isActive && (
                                    <Check className="mt-1 h-4 w-4 flex-shrink-0" style={{ color: profile.accent }} />
                                )}
                            </button>
                        );
                    })}
                    <p className="px-3 pt-1.5 pb-2 text-[11px] leading-snug text-gray-400 border-t border-gray-100 mt-1">
                        Your children, BHCS Cash and payment history are shared across both profiles.
                    </p>
                </div>
            )}
        </div>
    );
}
